import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Step,
  StepLabel,
  Stepper,
  StepConnector,
  stepConnectorClasses,
  Typography,
  CircularProgress,
} from "@mui/material";
import AppTablePagination from "../../component/common/appTablePagination";
import WatchLaterOutlinedIcon from "@mui/icons-material/WatchLaterOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

import { styled } from "@mui/material/styles";
import DeliveryDetailsModal from "./DeliveryDetailsModal";
import { useTranslation } from "react-i18next";
import deliveryServices from "../../../services/DeliveryService";
import {
  DeliveryDashboardData,
  DeliveryItem,
  DeliveryFilterParams,
} from "../../../types/deliveryManagement";
import { StepIconProps } from "@mui/material/StepIcon";
import { NJIM } from "../../../theme/njimTheme";

const DELIVERY_COMING_SOON = true;


const CustomConnector = styled(StepConnector)(() => ({
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    borderRadius: 2,
    backgroundColor: "rgba(226,232,240,0.6)",
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    background: `linear-gradient(90deg, ${NJIM.success} 0%, ${NJIM.success} 100%)`,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    background: `linear-gradient(90deg, ${NJIM.success} 0%, ${NJIM.success} 100%)`,
  },
}));

const DeliveryManagement = () => {
  const { t } = useTranslation();
  const [deliverDetailsOpen, setDeliverDetailsOpen] = React.useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryItem | null>(
    null
  );
  const [dashboardData, setDashboardData] =
    useState<DeliveryDashboardData | null>(null);
  const [deliveryList, setDeliveryList] = useState<DeliveryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<DeliveryFilterParams>({
    keyword: "",
    category: "",
    pageNumber: 1,
    pageSize: 10,
    sortDescending: false,
  });

  const steps = [
    t("deliveryManagement.confirmed"),
    t("deliveryManagement.onTheWay"),
    t("deliveryManagement.delivered"),
  ];

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await deliveryServices.getDeliveryDashboardData();
      const data: DeliveryDashboardData = response.data;
      setDashboardData(data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  }, []);

  const lastFiltersRef = React.useRef<string>("");

  const fetchDeliveryList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deliveryServices.getDeliveryList(filters);
      const data = response.data;
      setDeliveryList(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching delivery list:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const currentFilters = JSON.stringify(filters);

    if (lastFiltersRef.current === currentFilters) return;

    lastFiltersRef.current = currentFilters;
    fetchDeliveryList();
    fetchDashboardData();
  }, [filters, fetchDeliveryList, fetchDashboardData]);

  const getCurrentStep = (status: string) => {
    const s = status.toLowerCase().trim();
    if (s === "confirmed") return 0;
    if (s === "on the way" || s === "ontheway") return 1;
    if (s === "delivered") return 2;
    return -1;
  };

  const handleViewDetails = (delivery: DeliveryItem) => {
    setSelectedDelivery(delivery);
    setDeliverDetailsOpen(true);
  };

  const StepIcon: React.FC<StepIconProps & { activeStep: number }> = ({
    icon,
    activeStep,
  }) => {
    const stepNumber = Number(icon);
    //conver ReactNode -> numbers
    const gray = NJIM.textMuted;
    const green = NJIM.success;
    const blue = NJIM.info;

    const baseStyle: React.CSSProperties = {
      fontSize: 22,
      padding: "10px",
      borderRadius: "12px",
      background: "rgba(226,232,240,0.4)",
      transition: "all 0.3s ease",
    };

    if (stepNumber === 1) {
      return (
        <CheckCircleOutlineOutlinedIcon
          style={{
            ...baseStyle,
            color: activeStep >= 0 ? green : gray,
            background: activeStep >= 0 ? "rgba(5,150,105,0.1)" : "rgba(226,232,240,0.4)",
          }}
        />
      );
    }

    if (stepNumber === 2) {
      return (
        <LocalShippingOutlinedIcon
          style={{
            ...baseStyle,
            color: activeStep >= 1 ? blue : gray,
            background: activeStep >= 1 ? "rgba(14,165,233,0.1)" : "rgba(226,232,240,0.4)",
          }}
        />
      );
    }

    return (
      <LocationOnOutlinedIcon
        style={{
          ...baseStyle,
          color: activeStep === 2 ? green : gray,
          background: activeStep === 2 ? "rgba(5,150,105,0.1)" : "rgba(226,232,240,0.4)",
        }}
      />
    );
  };

  if (DELIVERY_COMING_SOON) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: NJIM.textPrimary,
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.35rem", sm: "1.5rem" },
          }}
        >
          {t("deliveryManagement.title")}
        </Typography>
        <Typography sx={{ color: NJIM.textSecondary, mb: 3, fontSize: "15px" }}>
          {t("deliveryManagement.trackDeliveries")}
        </Typography>

        <Card
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: "center",
            borderRadius: "12px",
            border: `1px solid ${NJIM.border}`,
            boxShadow: NJIM.elevation,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: NJIM.mintWash,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <LocalShippingOutlinedIcon sx={{ fontSize: 36, color: NJIM.mint }} />
          </Box>
          <Typography
            component="span"
            sx={{
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              borderRadius: "999px",
              bgcolor: NJIM.mintWash,
              color: NJIM.ink,
              border: `1px solid rgba(93,183,167,0.35)`,
              mb: 1.5,
            }}
          >
            {t("common.comingSoon")}
          </Typography>
          <Typography sx={{ color: NJIM.textSecondary, fontSize: 15, maxWidth: 420, mx: "auto" }}>
            {t("deliveryManagement.comingSoonDescription")}
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary, letterSpacing: "-0.3px", fontSize: "1.5rem" }}>
          {t("deliveryManagement.title")}
        </Typography>
        <Typography sx={{ color: NJIM.textSecondary, mb: 3, fontSize: "15px" }}>
          {t("deliveryManagement.trackDeliveries")}
        </Typography>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.3s ease",
                "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.06) !important", transform: "translateY(-2px)" },
              }}
            >
              <Box
                sx={{
                  background: "rgba(245,158,11,0.08)",
                  borderRadius: "12px",
                  p: 1.2,
                  display: "flex",
                }}
              >
                <WatchLaterOutlinedIcon sx={{ color: NJIM.warning, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, fontWeight: 500 }}>
                  {t("deliveryManagement.confirmed")}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "22px", mt: 0.3, color: NJIM.textPrimary }}>
                  {dashboardData?.confirmed ?? 0}
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.3s ease",
                "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.06) !important", transform: "translateY(-2px)" },
              }}
            >
              <Box
                sx={{
                  background: "rgba(14,165,233,0.08)",
                  borderRadius: "12px",
                  p: 1.2,
                  display: "flex",
                }}
              >
                <LocalShippingOutlinedIcon sx={{ color: NJIM.info, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, fontWeight: 500 }}>
                  {t("deliveryManagement.onTheWay")}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "22px", mt: 0.3, color: NJIM.textPrimary }}>
                  {dashboardData?.onTheWay ?? 0}
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.3s ease",
                "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.06) !important", transform: "translateY(-2px)" },
              }}
            >
              <Box
                sx={{
                  background: "rgba(5,150,105,0.08)",
                  borderRadius: "12px",
                  p: 1.2,
                  display: "flex",
                }}
              >
                <CheckCircleOutlineOutlinedIcon sx={{ color: NJIM.success, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, fontWeight: 500 }}>
                  {t("deliveryManagement.delivered")}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "22px", mt: 0.3, color: NJIM.textPrimary }}>
                  {dashboardData?.delivered ?? 0}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Delivery List */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress size={44} sx={{ color: NJIM.ink }} />
          </Box>
        ) : (
          <>
            {deliveryList.map((delivery) => {
              const rawStep = getCurrentStep(delivery.deliveryStatus);
              const activeStep = rawStep < 0 ? -1 : rawStep;
              const podAvailable = false;

              const getDeliveryStatusStyle = () => {
                const s = String(delivery.deliveryStatus ?? "").toLowerCase();
                if (s === "delivered") return { bg: "rgba(5,150,105,0.08)", color: NJIM.success, border: "rgba(5,150,105,0.2)" };
                if (s === "ontheway" || s === "on the way") return { bg: "rgba(14,165,233,0.08)", color: NJIM.info, border: "rgba(14,165,233,0.2)" };
                return { bg: "rgba(245,158,11,0.08)", color: NJIM.warning, border: "rgba(245,158,11,0.2)" };
              };
              const statusStyle = getDeliveryStatusStyle();

              return (
                <Card
                  key={delivery.id}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.06) !important" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "15px", color: NJIM.textPrimary }}>
                      {delivery.title}
                    </Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.4,
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 600,
                        background: statusStyle.bg,
                        color: statusStyle.color,
                        border: `1px solid ${statusStyle.border}`,
                      }}
                    >
                      {String(delivery.deliveryStatus ?? "").replace(/([a-z])([A-Z])/g, "$1 $2")}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 1.5,
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: 13,
                        color: NJIM.textSecondary,
                      }}
                    >
                      <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: NJIM.textMuted }} />
                      {t("deliveryManagement.driver")}
                      {""} -
                    </Typography>

                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: 13,
                        color: NJIM.textSecondary,
                      }}
                    >
                      <WatchLaterOutlinedIcon sx={{ fontSize: 18, color: NJIM.textMuted }} />
                      {String(delivery.deliveryStatus ?? "").replace(/([a-z])([A-Z])/g, "$1 $2")}
                    </Typography>

                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: 13,
                        color: podAvailable ? NJIM.success : NJIM.textMuted,
                      }}
                    >
                      <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                      {t("deliveryManagement.podAvailable")}
                    </Typography>

                    <Button
                      variant="outlined"
                      onClick={() => handleViewDetails(delivery)}
                      disabled
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "12px",
                        borderRadius: "8px",
                        borderColor: "rgba(226,232,240,0.8)",
                        color: NJIM.textSecondary,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: NJIM.ink,
                          color: NJIM.ink,
                          background: "rgba(10,61,120,0.04)",
                        },
                      }}
                    >
                      {t("deliveryManagement.viewDetails")}
                    </Button>
                  </Box>

                  <Stepper
                    activeStep={activeStep}
                    connector={<CustomConnector />}
                    sx={{ mt: 2.5, px: 1 }}
                  >
                    {steps.map((label, index) => (
                      <Step key={index}>
                        <StepLabel
                          StepIconComponent={(props) => (
                            <StepIcon {...props} activeStep={activeStep} />
                          )}
                          sx={{
                            "& .MuiStepLabel-label": {
                              fontSize: "12px",
                              fontWeight: 500,
                              color: activeStep < index ? NJIM.textMuted : NJIM.textSecondary,
                            },
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Card>
              );
            })}

            {/* Pagination */}
            {totalCount > 0 && (
              <Grid
                style={{
                  justifyContent: "flex-end",
                  alignItems: "center",
                  display: "flex",
                  marginTop: 16,
                  padding: 8,
                }}
              >
                <AppTablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber - 1}
                  onPageChange={(_, newPage) =>
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: newPage + 1,
                    }))
                  }
                  onRowsPerPageChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      pageSize: parseInt(e.target.value, 10),
                      pageNumber: 1,
                    }))
                  }
                  sx={{
                    color: NJIM.textSecondary,
                    "& .MuiSelect-icon": { color: NJIM.textSecondary },
                    "& .MuiSvgIcon-root": { color: NJIM.textSecondary },
                  }}
                />
              </Grid>
            )}
          </>
        )}
      </Box>
      {/* Modal for View Details */}
      <DeliveryDetailsModal
        open={deliverDetailsOpen}
        onClose={() => {
          setDeliverDetailsOpen(false);
          setSelectedDelivery(null);
        }}
        delivery={selectedDelivery}
      />
    </>
  );
};

export default DeliveryManagement;
